import { MailOutlined } from '@ant-design/icons'
import { Button, Card, Form, Input, Select, Space, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import { ROUTE } from '../../utils/constant'
import { EMAIL_STARTER_TEMPLATES } from '../../utils/emailTemplates/vnWomensDay2026'
import './EmailContentCreate.scss'

const slugify = value =>
	String(value || '')
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')

export const EmailContentCreate = () => {
	const history = useHistory()
	const location = useLocation()
	const [form] = Form.useForm()
	const [saving, setSaving] = useState(false)
	const [loading, setLoading] = useState(false)
	const [previewHTML, setPreviewHTML] = useState('')
	const [starterId, setStarterId] = useState(undefined)

	const templateId = useMemo(() => {
		const params = new URLSearchParams(location.search)
		const raw = params.get('id')
		const n = Number(raw)
		return Number.isFinite(n) && n > 0 ? n : null
	}, [location.search])

	const isEdit = templateId != null

	useEffect(() => {
		if (!templateId) {
			form.setFieldsValue({
				emailType: 'transactional',
				status: 'draft',
				htmlBody: '<p>Hello from WordsMine</p>',
				textBody: 'Hello from WordsMine',
			})
			setPreviewHTML('<p>Hello from WordsMine</p>')
			return
		}
		let cancelled = false
		;(async () => {
			setLoading(true)
			try {
				const tpl = await api.emailAdminApi.getTemplate(templateId)
				if (cancelled) return
				form.setFieldsValue({
					name: tpl.name,
					slug: tpl.slug,
					emailType: tpl.emailType,
					status: tpl.status,
					subject: tpl.subject,
					htmlBody: tpl.htmlBody,
					textBody: tpl.textBody,
				})
				setPreviewHTML(tpl.htmlBody || '')
			} catch (err) {
				message.error(err?.message || 'Could not load template.')
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [templateId, form])

	const onNameBlur = () => {
		const name = form.getFieldValue('name')
		const slug = form.getFieldValue('slug')
		if (!slug && name) {
			form.setFieldsValue({ slug: slugify(name) })
		}
	}

	const applyStarter = starterKey => {
		setStarterId(starterKey)
		if (!starterKey) return
		const starter = EMAIL_STARTER_TEMPLATES.find(item => item.id === starterKey)
		if (!starter) return
		form.setFieldsValue({
			name: starter.name,
			slug: starter.slug,
			emailType: starter.emailType,
			status: starter.status,
			subject: starter.subject,
			htmlBody: starter.htmlBody,
			textBody: starter.textBody,
		})
		setPreviewHTML(starter.htmlBody || '')
	}

	const handleSubmit = async values => {
		setSaving(true)
		const payload = {
			name: values.name.trim(),
			slug: values.slug.trim(),
			emailType: values.emailType,
			status: values.status,
			subject: values.subject.trim(),
			htmlBody: values.htmlBody || '',
			textBody: values.textBody || '',
		}
		try {
			if (isEdit) {
				await api.emailAdminApi.updateTemplate(templateId, payload)
				message.success('Template updated.')
			} else {
				await api.emailAdminApi.createTemplate(payload)
				message.success('Template created.')
			}
			history.push(`${ROUTE.EMAIL_CONTENT_MANAGEMENT}${ROUTE.LIST}`)
		} catch (err) {
			message.error(err?.message || 'Could not save template.')
		} finally {
			setSaving(false)
		}
	}

	return (
		<ListPageShell className="email-content-create">
			<ListPageIntro
				icon={MailOutlined}
				title={isEdit ? 'Edit email template' : 'Create email template'}
				subtitle="Save reusable subject + HTML/text content. Only active templates can be sent."
			/>
			<Card loading={loading} bordered className="email-content-create__card">
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					onValuesChange={(_, all) => {
						setPreviewHTML(all.htmlBody || '')
					}}
				>
					{!isEdit && (
						<Form.Item
							label="Start from template"
							extra="Optional. Loads a ready-made marketing email you can edit before saving."
						>
							<Select
								allowClear
								placeholder="Blank template"
								value={starterId}
								onChange={applyStarter}
								options={EMAIL_STARTER_TEMPLATES.map(item => ({
									value: item.id,
									label: item.name,
								}))}
							/>
						</Form.Item>
					)}
					<Form.Item
						label="Name"
						name="name"
						rules={[{ required: true, message: 'Name is required' }]}
					>
						<Input placeholder="Welcome email" onBlur={onNameBlur} />
					</Form.Item>
					<Form.Item
						label="Slug"
						name="slug"
						rules={[{ required: true, message: 'Slug is required' }]}
						extra="Lowercase kebab-case, unique."
					>
						<Input placeholder="welcome-email" />
					</Form.Item>
					<Space size={16} style={{ display: 'flex' }} wrap>
						<Form.Item
							label="Type"
							name="emailType"
							rules={[{ required: true }]}
							style={{ minWidth: 180 }}
						>
							<Select
								options={[
									{ value: 'transactional', label: 'Transactional' },
									{ value: 'marketing', label: 'Marketing' },
									{ value: 'newsletter', label: 'Newsletter' },
									{ value: 'other', label: 'Other' },
								]}
							/>
						</Form.Item>
						<Form.Item
							label="Status"
							name="status"
							rules={[{ required: true }]}
							style={{ minWidth: 160 }}
						>
							<Select
								options={[
									{ value: 'draft', label: 'Draft' },
									{ value: 'active', label: 'Active' },
									{ value: 'archived', label: 'Archived' },
								]}
							/>
						</Form.Item>
					</Space>
					<Form.Item
						label="Subject"
						name="subject"
						rules={[{ required: true, message: 'Subject is required' }]}
					>
						<Input placeholder="Subject line" maxLength={998} showCount />
					</Form.Item>
					<Form.Item
						label="HTML body"
						name="htmlBody"
						rules={[
							{
								validator: async (_, value) => {
									const text = form.getFieldValue('textBody')
									if (!String(value || '').trim() && !String(text || '').trim()) {
										throw new Error('HTML or text body is required')
									}
								},
							},
						]}
					>
						<Input.TextArea rows={10} placeholder="<p>Hello...</p>" />
					</Form.Item>
					<Form.Item label="Text body (fallback)" name="textBody">
						<Input.TextArea rows={4} placeholder="Plain text fallback" />
					</Form.Item>
					<div className="email-content-create__preview-label">HTML preview</div>
					<div
						className="email-content-create__preview"
						dangerouslySetInnerHTML={{ __html: previewHTML || '<em>—</em>' }}
					/>
					<Space>
						<Button type="primary" htmlType="submit" loading={saving}>
							{isEdit ? 'Update template' : 'Create template'}
						</Button>
						<Button
							onClick={() =>
								history.push(`${ROUTE.EMAIL_CONTENT_MANAGEMENT}${ROUTE.LIST}`)
							}
						>
							Cancel
						</Button>
					</Space>
				</Form>
			</Card>
		</ListPageShell>
	)
}
